import React from "react";
import { useNavigate } from "react-router-dom";

/* --- Broad category matcher --- */
const BROAD_WORDS = ["Layer", "Portfolio", "Holdings", "Ecosystem", "Made in USA", "Smart Contract Platform", 
  "Launchpad", "Index"
];
const BROAD_RE = new RegExp(`\\b(${BROAD_WORDS.map(w => w.replace(/\s+/g, "\\s+")).join("|")})\\b`, "i");
const isBroadCategory = (name = "") => BROAD_RE.test(String(name));

/* --- Reusable combobox (unchanged except it just takes the filtered options you give it) --- */
function CategorySelect({
  value,
  onChange,
  options, // [{ name, pointCount, slug }]
  placeholder = "Type to search categories…",
  className = "filter-select",
  showCounts = true,
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState(value || "");
  const [highlight, setHighlight] = React.useState(0);
  const wrapRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const list = React.useMemo(() => {
    const q = (query || "").toLowerCase().trim();
    const base = options || [];
    if (!q) return base.slice(0, 200);
    return base.filter(c => c.name.toLowerCase().includes(q)).slice(0, 200);
  }, [options, query]);

  React.useEffect(() => { setQuery(value || ""); }, [value]);

  React.useEffect(() => {
    function onDocClick(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  function selectCategory(name) {
    onChange?.(name);
    setQuery(name || "");
    setOpen(false);
  }
  function clearSelection() {
    onChange?.("");
    setQuery("");
    setOpen(false);
    inputRef.current?.focus();
  }
  function onKeyDown(e) {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) { setOpen(true); return; }
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight(h => Math.min(h + 1, Math.max(list.length - 1, 0))); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); const item = list[highlight]; if (item) selectCategory(item.name); }
    else if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
  }

  return (
    <div ref={wrapRef} style={{ position: "relative", width: 320 }}>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <input
          ref={inputRef}
          className={className}
          placeholder={placeholder}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          style={{ width:"100%", padding:"6px 10px", borderRadius:"6px",
                   border:"1px solid #555", background:"#111", color:"#fff" }}
        />
        {value && (
          <button
            onClick={clearSelection}
            title="Clear"
            className="hype-button"
          >
            ✕
          </button>
        )}
      </div>

      {open && (
        <div
          style={{
            position:"absolute", left:0, right:0, zIndex:50, background:"#111",
            border:"1px solid #555", borderRadius:8, marginTop:6, maxHeight:320,
            overflowY:"auto", boxShadow:"0 8px 24px rgba(0,0,0,0.4)"
          }}
        >
          <div
            onMouseDown={(e)=>e.preventDefault()}
            onClick={() => selectCategory("")}
            style={{ padding:"8px 10px", cursor:"pointer", background: value ? "transparent" : "#1b1b1b", fontWeight: value ? 400 : 600 }}
          >
            All
          </div>
          {list.length === 0 && <div style={{ padding:"8px 10px", opacity:0.7 }}>No matches</div>}
          {list.map((c, idx) => (
            <div
              key={c.slug || c.name}
              onMouseDown={(e)=>e.preventDefault()}
              onMouseEnter={() => setHighlight(idx)}
              onClick={() => selectCategory(c.name)}
              style={{
                padding:"8px 10px", cursor:"pointer",
                background: idx === highlight ? "#1e293b" : "transparent",
                display:"flex", justifyContent:"space-between", gap:8
              }}
            >
              <span>{c.name}</span>
              {showCounts && <span style={{ opacity:0.7 }}>{c.pointCount ?? 0}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* --- Page --- */
export default function CategoryList() {
  const isLocal = window.location.hostname === "localhost";
  const baseUrl = isLocal ? "http://localhost:5000" : "https://www.trendscout.club";
  const navigate = useNavigate();

  const [cats, setCats] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [sortKey, setSortKey] = React.useState("score"); // score | tokens | updated
  const [error, setError] = React.useState(null);

  // table search (optional)
  const [filterQ, setFilterQ] = React.useState("");

  // combobox value; selecting navigates straight to a category
  const [jumpValue, setJumpValue] = React.useState("");

  // NEW: toggle to show the broad/umbrella categories
  const [showBroad, setShowBroad] = React.useState(false); // default hidden

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await fetch(`${baseUrl}/api/categories`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        setCats(j.items || []);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [baseUrl]);

  // when user picks a category from the combobox, navigate
  React.useEffect(() => {
    if (jumpValue) {
      navigate(`/categories/${encodeURIComponent(jumpValue)}`);
    }
  }, [jumpValue, navigate]);

  // 1) hide broad categories unless toggled
  const visibleCats = React.useMemo(() => {
    if (showBroad) return cats;
    return (cats || []).filter(c => !isBroadCategory(c.name));
  }, [cats, showBroad]);

  const hiddenCount = React.useMemo(() => {
    return (cats || []).reduce((n, c) => n + (isBroadCategory(c.name) ? 1 : 0), 0);
  }, [cats]);

  // 2) sorting on the visible list
  const sorted = React.useMemo(() => {
    const arr = [...visibleCats];
    if (sortKey === "tokens") return arr.sort((a,b) => (b.tokenCount||0) - (a.tokenCount||0));
    if (sortKey === "updated") return arr.sort((a,b) => new Date(b.lastUpdatedAt||0) - new Date(a.lastUpdatedAt||0));
    return arr.sort((a,b) => (b.pointCount||0) - (a.pointCount||0)); // score (7d points)
  }, [visibleCats, sortKey]);

  // 3) inline name filter
  const filtered = React.useMemo(() => {
    const q = filterQ.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter(c => c.name.toLowerCase().includes(q));
  }, [sorted, filterQ]);

  return (
    <div className="ai-suggestion-list">
      <div className="macro-section">
        <h3>📚 Categories</h3>

        <div
          className="hype-filter-section"
          style={{ marginBottom: 10, gap: 12, display:"flex", flexWrap:"wrap", alignItems:"center" }}
        >
          {/* Jump-to combobox (uses visible list so it also hides broad by default) */}
          <div className="filter-group">
            <span className="filter-label">Jump to</span>
            <CategorySelect
              value={jumpValue}
              onChange={setJumpValue}
              options={visibleCats}
              placeholder="Search & open a category…"
            />
          </div>

          {/* Inline table filter */}
          <div className="filter-group">
            <span className="filter-label">Filter list</span>
            <input
              type="text"
              value={filterQ}
              onChange={(e)=>setFilterQ(e.target.value)}
              placeholder="Type to filter displayed rows…"
              className="filter-select"
              style={{ padding:"6px 10px", borderRadius:"6px", border:"1px solid #555", background:"#111", color:"#fff", width:280 }}
            />
          </div>

          {/* Sort */}
          <div className="filter-group">
            <span className="filter-label">Sort by</span>
            <select className="filter-select" value={sortKey} onChange={e => setSortKey(e.target.value)}>
              <option value="score">Score (7d points)</option>
              <option value="tokens"># Tokens</option>
              <option value="updated">Last Updated</option>
            </select>
          </div>

          {/* NEW: toggle broad categories */}
          <label className="custom-checkbox" style={{ display:"flex", alignItems:"center", gap:8, cursor:"pointer" }}>
            <input
              type="checkbox"
              checked={showBroad}
              onChange={() => setShowBroad(v => !v)}
            />
            <span className="checkmark" />
            Show broad categories
          </label>

          {!showBroad && hiddenCount > 0 && (
            <span style={{ opacity:0.75 }}>
              (Hiding {hiddenCount} umbrella categories: {BROAD_WORDS.join(", ")})
            </span>
          )}
        </div>

        {loading && <div className="loading-banner">Loading…</div>}
        {error && <div className="error-banner">{error}</div>}

        <div className="table-wrap">
          <table className="hype-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>7d Points</th>
                <th># Tokens</th>
                <th>Last Updated</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.slug}
                    style={{ cursor:"pointer" }}
                    onClick={() => navigate(`/categories/${encodeURIComponent(c.name)}`)}>
                  <td><span className="hype-symbol">{c.name}</span></td>
                  <td>{c.pointCount ?? 0}</td>
                  <td>{c.tokenCount ?? 0}</td>
                  <td>{c.lastUpdatedAt ? new Date(c.lastUpdatedAt).toLocaleString() : "—"}</td>
                </tr>
              ))}
              {filtered.length === 0 && !loading && !error && (
                <tr><td colSpan={4} style={{ opacity:0.7, padding:"10px" }}>No categories match your filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: 10 }}>
          <button className="hype-button" onClick={() => navigate("/hyped-alts")}>← Back to Hype</button>
        </div>
      </div>
                <div style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 12,
            fontSize: 12,
            color: "#9aa4b2"
          }}>
            <span>
              Data provided by{" "}
              <a
                href="https://www.coingecko.com/en/api"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#93c5fd", textDecoration: "underline" }}
                aria-label="CoinGecko API (opens in a new tab)"
              >
                CoinGecko
              </a>
            </span>
          </div>
    </div>
  );
}
